/**
 * Rename Group Chat Modal Component
 * 
 * Modal for renaming a group chat conversation.
 */

import React, { useState, useEffect } from 'react';
import { Modal, Input, App } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Conversation } from '@municipality/types';

interface RenameGroupChatModalProps {
  open: boolean;
  conversation: Conversation | null;
  onClose: () => void;
  onRename: (conversationId: string, newName: string) => Promise<void>;
}

export const RenameGroupChatModal: React.FC<RenameGroupChatModalProps> = ({
  open,
  conversation,
  onClose,
  onRename,
}) => {
  const { t } = useTranslation('chat');
  const { message } = App.useApp();
  const [newName, setNewName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Reset form when modal opens/closes or conversation changes
  useEffect(() => {
    if (open && conversation) {
      setNewName(conversation.name || '');
    } else {
      setNewName('');
    }
  }, [open, conversation]);

  const handleRename = async () => {
    if (!conversation || !newName.trim()) {
      return;
    }

    // Don't rename if name hasn't changed
    if (newName.trim() === (conversation.name || '')) {
      onClose();
      return;
    }

    setIsRenaming(true);
    try {
      await onRename(conversation.id, newName.trim());
      message.success(t('renameGroupChat.success') || 'Group chat renamed successfully');
      onClose();
    } catch (error) {
      console.error('Failed to rename group chat:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to rename group chat';
      message.error(errorMessage);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleCancel = () => {
    setNewName(conversation?.name || '');
    onClose();
  };

  if (!conversation || conversation.type !== 'group') {
    return null;
  }

  return (
    <Modal
      title={t('renameGroupChat.title') || 'Rename Group Chat'}
      open={open}
      onOk={handleRename}
      onCancel={handleCancel}
      confirmLoading={isRenaming}
      okText={t('renameGroupChat.save') || 'Save'}
      cancelText={t('buttons.cancel', { ns: 'common' }) || 'Cancel'}
      okButtonProps={{ disabled: !newName.trim() }}
    >
      <Input
        placeholder={t('renameGroupChat.placeholder') || 'Enter group name'}
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        onPressEnter={handleRename}
        maxLength={100}
        autoFocus
      />
    </Modal>
  );
};

